import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@mui/material';
import { Flex, Typo, SimpleButton, Loading, theme } from '../../../ui';
import { media } from '../../../utils/media';
import { GUIDE_STEPS } from '../courseGuideState';

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    width: 100vw;
    height: 100vh;
    max-width: 100vw;
    max-height: 100vh;
    margin: 0;
    border-radius: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
`;

const Header = styled.div`
  padding: 16px 32px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.NEU_200};
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;

  ${media.lessThan('tablet')} {
    padding: 12px 16px;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 32px;

  ${media.lessThan('tablet')} {
    padding: 16px;
  }
`;

const ContentInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
`;

const Footer = styled.div`
  padding: 16px 32px;
  border-top: 1px solid ${({ theme }) => theme.colors.NEU_200};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;

  ${media.lessThan('tablet')} {
    padding: 12px 16px;
  }
`;

const DotsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Dot = styled.div`
  width: ${({ $active }) => ($active ? '24px' : '8px')};
  height: 8px;
  border-radius: 4px;
  background: ${({ $active, $completed, theme }) =>
    $active ? theme.colors.PRIMARY : $completed ? theme.colors.PRIMARY : theme.colors.NEU_200};
  opacity: ${({ $active, $completed }) => ($active ? 1 : $completed ? 0.4 : 1)};
  display: ${({ $hidden }) => ($hidden ? 'none' : 'block')};
  transition: all 0.25s ease;
`;

const ProgressBar = styled.div`
  height: 4px;
  background: ${({ theme }) => theme.colors.NEU_100};
  width: 100%;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.colors.PRIMARY};
  width: ${({ $progress }) => $progress}%;
  transition: width 0.3s ease;
`;

const PopulatingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: ${theme.colors.NEU_0};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 20;
  gap: 16px;
`;

const ProgressBarWrapper = styled.div`
  width: 200px;
`;

const STEP_COUNT = 4; // Total steps: 0,1,2,3

const GuideOverlay = ({
  currentStep,
  startStep = 0,
  onNext,
  onBack,
  onSkip,
  onFinish,
  isPopulating,
  populationProgress,
  isNextDisabled,
  children,
}) => {
  const { t } = useTranslation();
  const isLastStep = currentStep === GUIDE_STEPS.SETTINGS;

  return (
    <StyledDialog open fullScreen>
      {isPopulating && (
        <PopulatingOverlay>
          <Loading />
          <Typo.ContentSmallHeader>{t('courseGuide.building')}</Typo.ContentSmallHeader>
          <ProgressBarWrapper>
            <ProgressBar>
              <ProgressFill $progress={populationProgress} />
            </ProgressBar>
          </ProgressBarWrapper>
          <Typo.SmallText>{populationProgress}%</Typo.SmallText>
        </PopulatingOverlay>
      )}

      <Header>
        <SimpleButton variant='ghost' size='small' icon='Close' onClick={onSkip} />
      </Header>

      <Content>
        <ContentInner>{children}</ContentInner>
      </Content>

      <Footer>
        <SimpleButton variant='ghost' onClick={onSkip}>
          {t('courseGuide.skipGuide')}
        </SimpleButton>

        <DotsWrapper>
          {Array.from({ length: STEP_COUNT }, (_, i) => (
            <Dot
              key={i}
              $active={currentStep === i}
              $completed={i < currentStep}
              $hidden={i < startStep}
            />
          ))}
        </DotsWrapper>

        <Flex gap='10'>
          <SimpleButton variant='secondary' onClick={onBack} disabled={currentStep <= startStep} startIcon='Back'>
            {t('globals.previous')}
          </SimpleButton>
          {isLastStep ? (
            <SimpleButton variant='primary' onClick={onFinish} disabled={isNextDisabled} startIcon='Check'>
              {t('courseGuide.finishAndBuild')}
            </SimpleButton>
          ) : (
            <SimpleButton variant='primary' onClick={onNext} disabled={isNextDisabled} endIcon='Next'>
              {t('globals.next')}
            </SimpleButton>
          )}
        </Flex>
      </Footer>
    </StyledDialog>
  );
};

export default GuideOverlay;
